import React, { useEffect, useState } from 'react';
import { KeenIcon } from '../keenicons';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';

type Props = {
    file: any;
    index: number;
    onDelete?: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    isEnableMove: boolean;
    showCopyUrl?: boolean;
};

const fileTypeIconMap: Record<string, string> = {
    pdf: 'pdf.svg',
    doc: 'doc.svg',
    docx: 'doc.svg',
    xls: 'xls.svg',
    xlsx: 'xls.svg',
    ppt: 'ppt.svg',
    pptx: 'ppt.svg',
    jpg: 'image.svg',
    jpeg: 'image.svg',
    png: 'image.svg',
    gif: 'image.svg',
    zip: 'zip.svg',
    txt: 'txt.svg',
    mp4: 'video.svg',
    mp3: 'audio.svg',
    css: 'css.svg',
    js: 'js.svg',
    json: 'json.png',
    default: 'font.svg',
    svg: 'svg.svg',
    ttf: 'ttf.png',
    otf: 'otf.png',
    woff: 'woff.png',
    woff2: 'woff.png'

};

// ---------- Helpers ----------

const getFileIcon = (fileName: string): string => {
    const ext = fileName?.split('.').pop()?.toLowerCase() || '';
    return fileTypeIconMap[ext] || fileTypeIconMap['default'];
};

const getFileName = (path: string): string => {
    return path?.split('/').pop() || '';
};

const formatBytes = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = parseFloat((bytes / Math.pow(1024, i)).toFixed(1));
    return `${size} ${sizes[i]}`;
};

const formatDate = (date: Date): string => {
    return date.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

// ---------- Main Component ----------

const UploadFileItem = ({ file, onDelete, onMoveUp, onMoveDown, isEnableMove, showCopyUrl }: Props) => {
    const intl = useIntl()
    const [fileData, setFileData] = useState({
        name: '',
        icon: '',
        size: '',
        date: '',
        url: "",
    });
    const baseUrl = process.env.NEXT_PUBLIC_IMAGE_BASE_URL;
    useEffect(() => {
        const parseFile = async () => {
            // EXT: string
            if (typeof file === 'string') {
                const resolvedUrl = file.startsWith('EXT:')
                    ? file.replace(/^EXT:/, baseUrl || '')
                    : `${baseUrl || ''}${file}`;

                const name = getFileName(resolvedUrl);
                const icon = getFileIcon(name);

                try {
                    // const resp = await fetch(resolvedUrl);
                    const resp = await fetch(resolvedUrl, {
                        method: 'HEAD'
                    });
                    const size = parseInt(resp.headers.get('content-length') || '0');
                    const lastModified = resp.headers.get('last-modified');



                    const modDate = lastModified ? new Date(lastModified) : new Date();
                    setFileData({
                        name,
                        icon,
                        size: formatBytes(size),
                        date: formatDate(modDate),
                        url: resolvedUrl,
                    });

                } catch (err) {

                    setFileData({ name, icon, size: '0 Byte', date: formatDate(new Date()), url: resolvedUrl });
                }
            }

            else if (typeof file === 'object' && file !== null) {
                const size = parseInt(file?.size || '0');
                const modDate = file?.date ? new Date(file?.date) : new Date();
                const name = getFileName(file?._original || file?.thumbnail || file?.original);
                const icon = getFileIcon(name);
                const url = file?._original ? file?._original : file?.original ? `${baseUrl || ''}${file}` : null;

                setFileData({
                    name,
                    icon,
                    size: formatBytes(size),
                    date: formatDate(modDate),
                    url: url,
                });
            }

            else if (file instanceof File || file?.file instanceof File) {
                const actualFile = file instanceof File ? file : file.file;
                setFileData({
                    name: actualFile.name,
                    icon: getFileIcon(actualFile.name),
                    size: formatBytes(actualFile.size),
                    date: formatDate(new Date()),
                    url: URL.createObjectURL(actualFile),
                });
            }

            // Fallback
            else {
                setFileData({
                    name: 'Unknown file',
                    icon: fileTypeIconMap['default'],
                    size: '0 Byte',
                    date: formatDate(new Date()),
                    url: '',
                });
            }
        };

        parseFile();
    }, [file]);
    const handleCopyUrl = async () => {
        if (fileData.url) {
            await navigator.clipboard.writeText(fileData.url);
            toast.success(intl.formatMessage({ id: "FILE_URL_COPIED" }));
        }
    };
    return (
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 shadow-sm  mb-2">
            <div className="flex items-center gap-3 overflow-hidden">
                <img
                    src={fileData.icon == "image.svg" ? fileData?.url ? `${fileData?.url}` : `/assets/media/file-types/${fileData.icon}` : `/assets/media/file-types/${fileData.icon}`}
                    alt="file type"
                    className="w-8 h-8 object-contain"
                />
                <div className="flex flex-col truncate">
                    <span className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary truncate">
                        {fileData.name}
                    </span>
                    <span className="text-xs text-gray-600">
                        {fileData.size}&nbsp;{fileData.date}
                    </span>
                </div>
            </div>

            <div className="flex gap-2 shrink-0">
                {
                    isEnableMove ?
                        <span className="btn btn-sm btn-icon btn-light" onClick={onMoveUp} title="Move Up">
                            <KeenIcon icon="double-up" />
                        </span>
                        : null
                }
                {
                    isEnableMove ?
                        <span className="btn btn-sm btn-icon btn-light" onClick={onMoveDown} title="Move Down">
                            <KeenIcon icon="double-down" />
                        </span>
                        : null
                }
                {
                    onDelete ?
                        <span className="btn btn-sm btn-icon btn-light text-danger" onClick={onDelete} title="Delete">
                            <KeenIcon icon="trash" />
                        </span>
                        : null
                }
                {showCopyUrl && (
                    <span
                        className="btn btn-sm btn-icon btn-light"
                        onClick={handleCopyUrl}
                        title="Copy URL"
                    >
                        <KeenIcon icon="copy" />
                    </span>
                )}
            </div>
        </div>
    );
};

export default UploadFileItem;

















